import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { DollarSign, FileText, TrendingUp } from 'lucide-react';
import SeoHead from '@/components/shared/SeoHead';
import PageHero from '@/components/shared/PageHero';
import AdSlot from '@/components/shared/AdSlot';
import AdFrame from '@/components/shared/AdFrame';
import SalaryTable from '@/components/shared/SalaryTable';
import FinancialStatusInsight from '@/components/shared/FinancialStatusInsight';
import FreeReportModal from '@/components/shared/FreeReportModal';
import { supabase } from '@/integrations/supabase/client';
import { STATE_MAPPINGS } from '@/lib/stateUtils';
import { toTitleCase } from '@/lib/utils';
import {
  INCOME_DATA_YEAR,
  US_FULLTIME_INCOME_PERCENTILES,
  estimateIncomePercentile,
  formatPercentileOrdinal,
  getReferenceTable,
  getTopPercentLabel,
  shareEarningAtLeast,
} from '@/lib/incomePercentile';
import { generateFAQPageSchema, generateMultipleSchemas, generateWebPageSchema } from '@/lib/schema';

/**
 * $100,000 is both the highest-intent query in this cluster ("what percent of
 * Americans make over 100k") and a sensible default, so the prerendered HTML
 * ships a complete worked answer rather than an empty form.
 */
const DEFAULT_SALARY = 100_000;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Math.round(value));

interface ComparableJob {
  occ_title?: string;
  title?: string;
  occupation_code?: string;
  soc?: string;
  mean_salary?: number;
  median_salary?: number;
  employment?: number;
}

const IncomePercentile = () => {
  const [salaryInput, setSalaryInput] = useState(String(DEFAULT_SALARY));
  const [showPdfModal, setShowPdfModal] = useState(false);

  const salary = useMemo(() => {
    const parsed = Number.parseFloat(salaryInput.replace(/[^0-9.]/g, ''));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }, [salaryInput]);

  const individualPercentile = estimateIncomePercentile(salary);
  const fullTimePercentile = estimateIncomePercentile(salary, US_FULLTIME_INCOME_PERCENTILES);
  const shareAbove = shareEarningAtLeast(salary);
  const topLabel = getTopPercentLabel(individualPercentile);
  const referenceTable = useMemo(() => getReferenceTable(), []);

  const { data: calculatorData } = useQuery({
    queryKey: ['income-percentile-jobs', salary],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('api-calculator', {
        body: { hourly: 0, annual: salary, area: '0000000', year: 2025 },
      });
      if (error) throw error;
      return data;
    },
    enabled: salary > 0,
  });

  const comparableJobs = useMemo(() => {
    const jobs: ComparableJob[] = calculatorData?.comparable_jobs || [];
    return jobs.map((job) => ({
      occ_title: job.occ_title || job.title || '',
      soc: job.occupation_code || job.soc || '',
      annual_mean_wage: job.mean_salary || 0,
      annual_median_wage: job.median_salary || 0,
      hourly_mean_wage: job.mean_salary ? Math.round((job.mean_salary / 2080) * 100) / 100 : 0,
      total_employment: job.employment || 0,
    }));
  }, [calculatorData]);

  // The closest-paying occupation anchors the free PDF report, which is
  // generated per occupation rather than per salary.
  const anchorJob = useMemo(() => {
    if (comparableJobs.length === 0) return null;
    return comparableJobs.reduce((closest, job) =>
      Math.abs(job.annual_mean_wage - salary) < Math.abs(closest.annual_mean_wage - salary)
        ? job
        : closest,
    );
  }, [comparableJobs, salary]);

  const faqItems = [
    {
      question: 'What percentile is a $100,000 salary?',
      answer: `A $100,000 individual income sits at roughly the ${formatPercentileOrdinal(estimateIncomePercentile(100_000))} percentile of all US earners, meaning it is higher than about ${Math.round(estimateIncomePercentile(100_000))}% of individual earners. Among full-time, year-round workers only, the same salary falls near the ${formatPercentileOrdinal(estimateIncomePercentile(100_000, US_FULLTIME_INCOME_PERCENTILES))} percentile, because that group excludes part-time and part-year earnings.`,
    },
    {
      question: 'What percent of Americans make over $100,000?',
      answer: `Roughly ${shareEarningAtLeast(100_000)}% of individual US earners make $100,000 or more per year. The figure is often quoted higher because household income counts every earner in a home, and most households have more than one.`,
    },
    {
      question: 'Why do income percentile calculators disagree with each other?',
      answer: 'Because they measure different populations. Individual income across all earners, individual income among full-time year-round workers, and household income each produce a different percentile for the same dollar figure. This page shows the two individual cuts side by side so the difference is explicit.',
    },
    {
      question: 'Is this the same as my salary percentile for my job?',
      answer: 'No. This page compares your income to all US earners. Your occupational percentile compares you to other people doing your specific job in your state, which is what the BLS Occupational Employment and Wage Statistics program measures. Use the Am I Underpaid checker for that comparison - a salary can be top 10% nationally and below median for its own occupation.',
    },
    {
      question: 'What income puts you in the top 1%?',
      answer: 'Around $400,000 in annual individual income places an earner in the top 1% nationally. The threshold is substantially higher in high-wage states and metro areas, and higher again for household rather than individual income.',
    },
  ];

  const canonical = 'https://wageatlas.com/income-percentile/';
  const schemas = generateMultipleSchemas([
    generateWebPageSchema(
      'Income Percentile Calculator',
      'See what percent of Americans earn your salary, with the occupations that actually pay it.',
      canonical,
      {},
    ),
    generateFAQPageSchema(faqItems),
  ]);

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="Income Percentile Calculator - Where Does Your Salary Rank? | Wage Atlas"
        description="See what percent of Americans earn your salary. Free income percentile calculator using Census and BLS data, with the real occupations that pay at that level."
        canonical={canonical}
        schemas={schemas}
      />

      <PageHero
        title="What Percent of Americans Earn Your Salary?"
        subtitle="Enter an annual income to see where it ranks among US earners - and which jobs actually pay it."
        maxWidth="max-w-3xl"
      >
        <AdFrame className="flex justify-center">
          <AdSlot slot="leaderboard" className="max-w-4xl" />
        </AdFrame>
      </PageHero>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Income Percentile Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="max-w-sm">
                  <Label htmlFor="annual-income">Annual income (pre-tax)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="annual-income"
                      type="number"
                      inputMode="numeric"
                      placeholder="100000"
                      value={salaryInput}
                      onChange={(e) => setSalaryInput(e.target.value)}
                      className="pl-10"
                      step="1000"
                      min="0"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Individual income, not household
                  </p>
                </div>

                {salary > 0 && (
                  <div className="space-y-4">
                    {topLabel && (
                      <Badge className="text-sm font-semibold">{topLabel} of US earners</Badge>
                    )}

                    <p className="text-lg">
                      <strong>{formatCurrency(salary)}</strong> ranks at about the{' '}
                      <strong>{formatPercentileOrdinal(individualPercentile)} percentile</strong> of
                      all US individual earners. Roughly <strong>{shareAbove}%</strong> of earners
                      make at least this much.
                    </p>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="p-4 rounded-lg bg-wage-neutral">
                        <div className="text-2xl font-bold text-wage-primary">
                          {formatPercentileOrdinal(individualPercentile)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Percentile among all individual earners
                        </div>
                      </div>
                      <div className="p-4 rounded-lg bg-wage-neutral">
                        <div className="text-2xl font-bold text-wage-primary">
                          {formatPercentileOrdinal(fullTimePercentile)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Percentile among full-time, year-round workers
                        </div>
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground">
                      The two figures differ because the full-time cut excludes part-time and
                      part-year earnings. Quoting one without the other is the most common reason
                      two people cite different percentiles for the same salary.
                    </p>
                  </div>
                )}

                <AdSlot slot="in-content" />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>US Income Percentile Reference Table</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="py-2 text-left font-medium">Percentile</th>
                        <th className="py-2 text-right font-medium">All earners</th>
                        <th className="py-2 text-right font-medium">Full-time, year-round</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referenceTable.map((row) => (
                        <tr key={row.percentile} className="border-b border-border/40">
                          <td className="py-2">{row.label}</td>
                          <td className="py-2 text-right tabular-nums">
                            {formatCurrency(row.individual)}
                          </td>
                          <td className="py-2 text-right tabular-nums">
                            {formatCurrency(row.fullTime)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Individual pre-tax earnings, {INCOME_DATA_YEAR} Census Current Population Survey
                  tables. Household income is a separate distribution and is not shown here.
                </p>
              </CardContent>
            </Card>

            {salary > 0 && <FinancialStatusInsight annualIncome={salary} contextLabel="This income" />}

            {comparableJobs.length > 0 && (
              <SalaryTable
                data={comparableJobs}
                title="Jobs That Actually Pay This"
                showAffiliateEvery={6}
                area="0000000"
                className="shadow-sm"
              />
            )}

            {anchorJob && (
              <Card className="border-wage-accent/30 bg-wage-accent/5">
                <CardContent className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1">
                    <h3 className="font-semibold mb-1">Free salary report</h3>
                    <p className="text-sm text-muted-foreground">
                      Get a free PDF breakdown of {toTitleCase(anchorJob.occ_title)} pay - the
                      occupation closest to {formatCurrency(salary)} - with percentiles, state
                      comparisons and employment data.
                    </p>
                  </div>
                  <Button onClick={() => setShowPdfModal(true)} className="shrink-0">
                    <FileText className="h-4 w-4 mr-2" />
                    Get the free report
                  </Button>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>How This Is Calculated</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <p>
                  Your percentile is estimated by linear interpolation between published Census
                  Current Population Survey earnings breakpoints for {INCOME_DATA_YEAR}. Two
                  distributions are shown: all individual earners, and full-time year-round workers
                  only.
                </p>
                <p>
                  This is a different question from the one BLS Occupational Employment and Wage
                  Statistics answers. OEWS tells you where you rank among people doing your job in
                  your state; this page tells you where you rank among all earners. A salary can
                  comfortably be top 10% nationally while sitting below the median for its own
                  occupation, which is why the two should be read together rather than
                  interchangeably.
                </p>
                <p>
                  The occupations listed above come from BLS OEWS national data and are real jobs
                  whose average pay falls close to the income entered. Follow any of them through to
                  see the full wage distribution and state-by-state breakdown.
                </p>
                <p className="text-muted-foreground">
                  Income percentiles cover pre-tax individual income. Household income counts every
                  earner in a home and produces a materially lower percentile for the same dollar
                  figure.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Frequently Asked Questions</CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible>
                  {faqItems.map((item, index) => (
                    <AccordionItem key={item.question} value={`faq-${index}`}>
                      <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
                      <AccordionContent className="text-muted-foreground">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Compare Wages by State</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  National percentiles hide large regional differences. Browse the wage data for any
                  state to see how pay for individual occupations compares locally.
                </p>
                <div className="flex flex-wrap gap-2">
                  {STATE_MAPPINGS.map((state) => (
                    <Button key={state.slug} variant="outline" size="sm" asChild>
                      <Link to={`/state/${state.slug}/`}>{state.name}</Link>
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <AdSlot slot="in-content" />
          </div>

          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Related Tools</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full justify-start text-sm" asChild>
                  <Link to="/am-i-underpaid/">Am I underpaid?</Link>
                </Button>
                <Button variant="outline" className="w-full justify-start text-sm" asChild>
                  <Link to="/calculators/salary/">Salary calculator</Link>
                </Button>
                <Button variant="outline" className="w-full justify-start text-sm" asChild>
                  <Link to="/calculators/tax/">Tax calculator</Link>
                </Button>
                <Button variant="outline" className="w-full justify-start text-sm" asChild>
                  <Link to="/insights/how-to-read-salary-percentiles/">
                    How to read salary percentiles
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <AdSlot slot="sidebar" />

            <AdFrame className="lg:hidden">
              <AdSlot slot="mobile-banner" />
            </AdFrame>
          </div>
        </div>
      </div>

      {anchorJob && (
        <FreeReportModal
          isOpen={showPdfModal}
          onClose={() => setShowPdfModal(false)}
          occupation={toTitleCase(anchorJob.occ_title)}
          area="United States"
          soc={anchorJob.soc}
          source="income_percentile"
        />
      )}
    </div>
  );
};

export default IncomePercentile;
